// openEHR BASE model — BMM package org.openehr.base.foundation_types.primitive_types
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { TYPE_REGISTRY } from "../_shared.ts";
import { Any } from "./foundation_types.ts";
import { List } from "./structure.ts";

/**
 * Abstract parent class of ordered types i.e. types on which the '<' operator is defined.
 */
export abstract class Ordered extends Any {
  /**
   * Arithmetic value comparison. Returns True if current object is less than \`_other_\`. This operator is effected and/or redefined in descendants to provide the appropriate ordering semantics for concrete types.
   *
   * In conjunction with \`=\`, enables the definition of the related functions \`_greater_than_()\` etc.
   * @param other - Parameter
   * @returns Result value
   */
  abstract less_than(other: Ordered): Boolean;

  /**
   * True if current object less than or equal to \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  less_than_or_equal(other: Ordered): Boolean {
    return new Boolean(
      this.less_than(other).value || this.is_equal(other).value,
    );
  }

  /**
   * True if current object greater than \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  greater_than(other: Ordered): Boolean {
    return new Boolean(
      !this.less_than(other).value && !this.is_equal(other).value,
    );
  }

  /**
   * True if current object greater than or equal to \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  greater_than_or_equal(other: Ordered): Boolean {
    return new Boolean(!this.less_than(other).value);
  }
}

/**
 * Type representing minimal interface of built-in String type, as used to represent textual data in any natural or formal language.
 */
export class String extends Ordered {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("STRING", String);
  }

  /**
   * The underlying primitive value.
   */
  value?: string;

  /**
   * Creates a new String instance.
   * @param val - The primitive value to wrap
   */
  constructor(val?: string) {
    super();
    this.value = val;
  }

  /**
   * Creates a String instance from a primitive value.
   * @param val - The primitive value to wrap
   * @returns A new String instance
   */
  static from(val?: string): String {
    return new String(val);
  }

  /**
   * Compares this String with another for value equality.
   * @param other - The object to compare with
   * @returns true if the values are equal
   */
  is_equal(other: any): Boolean {
    if (other instanceof String) {
      return new Boolean(this.value === other.value);
    }
    return new Boolean(false);
  }

  /**
   * True if string is empty, i.e. equal to "".
   * @returns Result value
   */
  is_empty(): Boolean {
    return new Boolean((this.value || "").length === 0);
  }

  /**
   * Number of characters in string.
   * @returns Result value
   */
  count(): Integer {
    const int = new Integer();
    int.value = (this.value || "").length;
    return int;
  }

  /**
   * True if string can be parsed as an integer.
   * @returns Result value
   */
  is_integer(): Boolean {
    const val = this.value || "";
    const num = Number(val);
    return new Boolean(
      !isNaN(num) && Number.isInteger(num) && val.trim() !== "",
    );
  }

  /**
   * Return the integer corresponding to the integer value represented in this string.
   * @returns Result value
   */
  as_integer(): Integer {
    const num = parseInt(this.value || "", 10);
    if (isNaN(num)) {
      throw new Error(`Cannot parse "${this.value}" as integer`);
    }
    return Integer.from(num);
  }

  /**
   * Concatenation operator - causes \`_other_\` to be appended to this string.
   * @param other - Parameter
   * @returns Result value
   */
  append(other: String): String {
    return String.from((this.value || "") + (other.value || ""));
  }

  /**
   * Convert string to lowercase.
   * @returns Result value
   */
  as_lower(): String {
    return String.from((this.value || "").toLowerCase());
  }

  /**
   * Convert string to uppercase.
   * @returns Result value
   */
  as_upper(): String {
    return String.from((this.value || "").toUpperCase());
  }

  /**
   * Lexical comparison of string content based on ordering in relevant character set.
   * @param other - Parameter
   * @returns Result value
   */
  less_than(other: Ordered): Boolean {
    if (!(other instanceof String)) {
      throw new Error("Cannot compare String with non-String");
    }
    return new Boolean((this.value || "") < (other.value || ""));
  }

  /**
   * Return True if this String contains \`_other_\` (case-sensitive).
   * @param other - Parameter
   * @returns Result value
   */
  contains(other: String): Boolean {
    return new Boolean((this.value || "").includes(other.value || ""));
  }

  /**
   * Extract a substring (1-based indexing in openEHR).
   * @param start - Start index (1-based)
   * @param end - End index (1-based)
   * @returns Result value
   */
  substring(start: Integer, end: Integer): String {
    const startIdx = (start.value || 1) - 1; // Convert to 0-based
    const endIdx = end.value || (this.value || "").length;
    return String.from((this.value || "").substring(startIdx, endIdx));
  }

  /**
   * Find the index of a substring (1-based indexing in openEHR).
   * @param pattern - Pattern to find
   * @param from - Start index (1-based)
   * @returns Result value (1-based index or -1 if not found)
   */
  index_of(pattern: String, from: Integer): Integer {
    const startIdx = (from.value || 1) - 1; // Convert to 0-based
    const foundIdx = (this.value || "").indexOf(pattern.value || "", startIdx);
    const result = new Integer();
    result.value = foundIdx === -1 ? -1 : foundIdx + 1; // Convert back to 1-based
    return result;
  }

  /**
   * Split string by delimiter.
   * @param delimiter - Delimiter to split by
   * @returns Result value
   */
  split(delimiter: String): List<String> {
    const parts = (this.value || "").split(delimiter.value || "");
    const list = new List<String>();
    for (const part of parts) {
      list.append(String.from(part));
    }
    return list;
  }
}

/**
 * A kind of String constrained to obey the syntax of RFC 3986.
 */
export class Uri extends String {
}

/**
 * Abstract parent class of numeric types, which are types which have various arithmetic and comparison operators defined.
 */
export abstract class Numeric extends Any {
  /**
   * Sum with \`_other_\` (commutative). Actual type of result depends on arithmetic balancing rules.
   * @param other - Parameter
   * @returns Result value
   */
  abstract add(other: Numeric): Numeric;

  /**
   * Result of subtracting \`_other_\`. Actual type of result depends on arithmetic balancing rules.
   * @param other - Parameter
   * @returns Result value
   */
  abstract subtract(other: Numeric): Numeric;

  /**
   * Product by \`_other_\`. Actual type of result depends on arithmetic balancing rules.
   * @param other - Parameter
   * @returns Result value
   */
  abstract multiply(other: Numeric): Numeric;

  /**
   * Divide by\`_other_\`. Actual type of result depends on arithmetic balancing rules.
   * @param other - Parameter
   * @returns Result value
   */
  abstract divide(other: Numeric): Numeric;

  /**
   * Expontiation of this by \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  abstract exponent(other: Numeric): Numeric;

  /**
   * Generate negative of current value.
   * @returns Result value
   */
  abstract negative(): Numeric;
}

/**
 * Abstract notional parent class of ordered, numeric types, which are types with both the \`_less_than_()\` and arithmetic functions defined.
 */
export abstract class Ordered_Numeric extends Ordered {
}

/**
 * Type representing minimal interface of built-in Integer type.
 */
export class Integer extends Ordered_Numeric {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("INTEGER", Integer);
  }

  /**
   * The underlying primitive value.
   */
  value?: number;

  /**
   * Creates a new Integer instance.
   * @param val - The primitive value to wrap
   */
  constructor(val?: number) {
    super();
    if (val !== undefined && val !== null && !Number.isInteger(val)) {
      throw new Error(`Integer value must be an integer, got: ${val}`);
    }
    this.value = val;
  }

  /**
   * Creates a Integer instance from a primitive value.
   * @param val - The primitive value to wrap
   * @returns A new Integer instance
   */
  static from(val?: number): Integer {
    return new Integer(val);
  }

  /**
   * Compares this Integer with another for value equality.
   * @param other - The object to compare with
   * @returns true if the values are equal
   */
  is_equal(other: any): Boolean {
    if (other instanceof Integer) {
      return new Boolean(this.value === other.value);
    }
    return new Boolean(false);
  }

  /**
   * Lexical comparison for integers.
   * @param other - Parameter
   * @returns Result value
   */
  less_than(other: Ordered): Boolean {
    if (!(other instanceof Integer)) {
      throw new Error("Cannot compare Integer with non-Integer");
    }
    const thisVal = this.value || 0;
    const otherVal = other.value || 0;
    return new Boolean(thisVal < otherVal);
  }

  /**
   * Integer addition.
   * @param other - Parameter
   * @returns Result value
   */
  add(other: Integer): Integer {
    const thisVal = this.value || 0;
    const otherVal = other.value || 0;
    return Integer.from(thisVal + otherVal);
  }

  /**
   * Integer subtraction.
   * @param other - Parameter
   * @returns Result value
   */
  subtract(other: Integer): Integer {
    const thisVal = this.value || 0;
    const otherVal = other.value || 0;
    return Integer.from(thisVal - otherVal);
  }

  /**
   * Integer multiplication.
   * @param other - Parameter
   * @returns Result value
   */
  multiply(other: Integer): Integer {
    const thisVal = this.value || 0;
    const otherVal = other.value || 0;
    return Integer.from(thisVal * otherVal);
  }

  /**
   * Integer division.
   * @param other - Parameter
   * @returns Result value
   */
  divide(other: Integer): number {
    const thisVal = this.value || 0;
    const otherVal = other.value || 1;
    if (otherVal === 0) {
      throw new Error("Division by zero");
    }
    return thisVal / otherVal;
  }

  /**
   * Integer modulo.
   * @param other - Parameter
   * @returns Result value
   */
  modulo(other: Integer): Integer {
    const thisVal = this.value || 0;
    const otherVal = other.value || 1;
    if (otherVal === 0) {
      throw new Error("Modulo by zero");
    }
    return Integer.from(thisVal % otherVal);
  }

  /**
   * Generate negative of current value.
   * @returns Result value
   */
  negative(): Integer {
    return Integer.from(-(this.value || 0));
  }

  /**
   * Integer exponentiation.
   * @param other - Parameter
   * @returns Result value
   */
  exponent(other: number): number {
    const thisVal = this.value || 0;
    return Math.pow(thisVal, other);
  }

  // modulo, less_than, negative, and is_equal are implemented above

  /**
   * Reference equality for reference types, value equality for value types.
   * @param other - Parameter
   * @returns Result value
   */
  override equal(other: Integer): Boolean {
    // Reference equality - same object
    return new Boolean(this === other);
  }
}

/**
 * Type used to represent double-precision decimal numbers. Corresponds to a double-precision floating point value in most languages.
 */
export class Double extends Ordered_Numeric {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("DOUBLE", Double);
  }

  /**
   * The underlying primitive value.
   */
  value?: number;

  /**
   * Creates a new Double instance.
   * @param val - The primitive value to wrap
   */
  constructor(val?: number) {
    super();
    this.value = val;
  }

  /**
   * Creates a Double instance from a primitive value.
   * @param val - The primitive value to wrap
   * @returns A new Double instance
   */
  static from(val?: number): Double {
    return new Double(val);
  }

  /**
   * Return the greatest integer no greater than the value of this object.
   * @returns Result value
   */
  floor(): Integer {
    const thisVal = this.value || 0;
    return Integer.from(Math.floor(thisVal));
  }

  /**
   * Double-precision real number addition.
   * @param other - Parameter
   * @returns Result value
   */
  add(other: number): number {
    const thisVal = this.value || 0;
    return thisVal + other;
  }

  /**
   * Double-precision real number subtraction.
   * @param other - Parameter
   * @returns Result value
   */
  subtract(other: number): number {
    const thisVal = this.value || 0;
    return thisVal - other;
  }

  /**
   * Double-precision real number multiplication.
   * @param other - Parameter
   * @returns Result value
   */
  multiply(other: number): number {
    const thisVal = this.value || 0;
    return thisVal * other;
  }

  /**
   * Double-precision real number division.
   * @param other - Parameter
   * @returns Result value
   */
  divide(other: number): number {
    const thisVal = this.value || 0;
    if (other === 0) {
      throw new Error("Division by zero");
    }
    return thisVal / other;
  }

  /**
   * Double-precision real number exponentiation.
   * @param other - Parameter
   * @returns Result value
   */
  exponent(other: number): number {
    const thisVal = this.value || 0;
    return Math.pow(thisVal, other);
  }

  /**
   * Returns True if current Double is less than \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: Ordered): Boolean {
    if (other instanceof Double) {
      const thisVal = this.value || 0;
      const otherVal = other.value || 0;
      return new Boolean(thisVal < otherVal);
    }
    return new Boolean(false);
  }

  /**
   * Generate negative of current Double value.
   * @returns Result value
   */
  negative(): number {
    const thisVal = this.value || 0;
    return -thisVal;
  }

  /**
   * Value equality: return True if \`this\` and \`_other_\` are attached to objects considered to be equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (other instanceof Double) {
      const thisVal = this.value || 0;
      const otherVal = other.value || 0;
      return new Boolean(thisVal === otherVal);
    }
    return new Boolean(false);
  }
}

/**
 * Type representing minimal interface of built-in Octet type.
 */
export class Octet extends Ordered {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("OCTET", Octet);
  }

  /**
   * The underlying primitive value.
   */
  value?: number;

  /**
   * Creates a new Octet instance.
   * @param val - The primitive value to wrap (0-255)
   */
  constructor(val?: number) {
    super();
    if (val !== undefined && val !== null && 
        (!Number.isInteger(val) || val < 0 || val > 255)) {
      throw new Error(`Octet value must be an integer between 0 and 255, got: ${val}`);
    }
    this.value = val;
  }

  /**
   * Creates an Octet instance from a primitive value.
   * @param val - The primitive value to wrap
   * @returns A new Octet instance
   */
  static from(val?: number): Octet {
    return new Octet(val);
  }

  /**
   * Returns True if current Octet is less than other.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: Ordered): Boolean {
    if (other instanceof Octet) {
      const thisVal = this.value || 0;
      const otherVal = other.value || 0;
      return new Boolean(thisVal < otherVal);
    }
    return new Boolean(false);
  }

  /**
   * Value equality: return True if this and other are equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (other instanceof Octet) {
      const thisVal = this.value || 0;
      const otherVal = other.value || 0;
      return new Boolean(thisVal === otherVal);
    }
    return new Boolean(false);
  }
}

/**
 * Type representing minimal interface of built-in Character type.
 */
export class Character extends Ordered {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("CHARACTER", Character);
  }

  /**
   * The underlying primitive value.
   */
  value?: string;

  /**
   * Creates a new Character instance.
   * @param val - The primitive value to wrap (single character)
   */
  constructor(val?: string) {
    super();
    if (val !== undefined && val !== null && val.length !== 1) {
      throw new Error(`Character value must be a single character, got: ${val}`);
    }
    this.value = val;
  }

  /**
   * Creates a Character instance from a primitive value.
   * @param val - The primitive value to wrap
   * @returns A new Character instance
   */
  static from(val?: string): Character {
    return new Character(val);
  }

  /**
   * Returns True if current Character is less than other.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: Ordered): Boolean {
    if (other instanceof Character) {
      const thisVal = this.value || "";
      const otherVal = other.value || "";
      return new Boolean(thisVal < otherVal);
    }
    return new Boolean(false);
  }

  /**
   * Value equality: return True if this and other are equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (other instanceof Character) {
      const thisVal = this.value || "";
      const otherVal = other.value || "";
      return new Boolean(thisVal === otherVal);
    }
    return new Boolean(false);
  }
}

/**
 * Type representing minimal interface of built-in Boolean type.
 */
export class Boolean extends Any {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("BOOLEAN", Boolean);
  }

  /**
   * The underlying primitive value.
   */
  value?: boolean;

  /**
   * Creates a new Boolean instance.
   * @param val - The primitive value to wrap
   */
  constructor(val?: boolean) {
    super();
    this.value = val;
  }

  /**
   * Creates a Boolean instance from a primitive value.
   * @param val - The primitive value to wrap
   * @returns A new Boolean instance
   */
  static from(val?: boolean): Boolean {
    return new Boolean(val);
  }

  /**
   * Compares this Boolean with another for value equality.
   * @param other - The object to compare with
   * @returns true if the values are equal
   */
  is_equal(other: any): Boolean {
    if (other instanceof Boolean) {
      return new Boolean(this.value === other.value);
    }
    return new Boolean(false);
  }

  /**
   * Logical conjunction of this with \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  conjunction(other: Boolean): Boolean {
    return new Boolean((this.value === true) && (other.value === true));
  }

  /**
   * Boolean semi-strict conjunction with \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  semistrict_conjunction(other: Boolean): Boolean {
    // Semi-strict: only evaluate other if this is true
    if (this.value !== true) {
      return new Boolean(false);
    }
    return new Boolean(other.value === true);
  }

  /**
   * Boolean disjunction with \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  disjunction(other: Boolean): Boolean {
    return new Boolean((this.value === true) || (other.value === true));
  }

  /**
   * Boolean semi-strict disjunction with \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  semistrict_disjunction(other: Boolean): Boolean {
    // Semi-strict: only evaluate other if this is false
    if (this.value === true) {
      return new Boolean(true);
    }
    return new Boolean(other.value === true);
  }

  /**
   * Boolean exclusive or with \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  exclusive_disjunction(other: Boolean): Boolean {
    return new Boolean((this.value === true) !== (other.value === true));
  }

  /**
   * Boolean implication of \`_other_\` (semi-strict)
   * @param other - Parameter
   * @returns Result value
   */
  implication(other: Boolean): Boolean {
    // A implies B is equivalent to (not A) or B
    if (this.value !== true) {
      return new Boolean(true);
    }
    return new Boolean(other.value === true);
  }

  /**
   * Boolean negation of the current value.
   * @returns Result value
   */
  negation(): Boolean {
    return new Boolean(this.value !== true);
  }
}

/**
 * Type used to represent decimal numbers. Corresponds to a single-precision floating point value in most languages.
 */
export class Real extends Ordered_Numeric {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("REAL", Real);
  }

  /**
   * The underlying primitive value.
   */
  value?: number;

  /**
   * Creates a new Real instance.
   * @param val - The primitive value to wrap
   */
  constructor(val?: number) {
    super();
    this.value = val;
  }

  /**
   * Creates a Real instance from a primitive value.
   * @param val - The primitive value to wrap
   * @returns A new Real instance
   */
  static from(val?: number): Real {
    return new Real(val);
  }

  /**
   * Return the greatest integer no greater than the value of this object.
   * @returns Result value
   */
  floor(): Integer {
    const thisVal = this.value || 0;
    return Integer.from(Math.floor(thisVal));
  }

  /**
   * Real number addition.
   * @param other - Parameter
   * @returns Result value
   */
  add(other: number): number {
    const thisVal = this.value || 0;
    return thisVal + other;
  }

  /**
   * Real number subtraction.
   * @param other - Parameter
   * @returns Result value
   */
  subtract(other: number): number {
    const thisVal = this.value || 0;
    return thisVal - other;
  }

  /**
   * Real number multiplication.
   * @param other - Parameter
   * @returns Result value
   */
  multiply(other: number): number {
    const thisVal = this.value || 0;
    return thisVal * other;
  }

  /**
   * Real number division.
   * @param other - Parameter
   * @returns Result value
   */
  divide(other: number): number {
    const thisVal = this.value || 0;
    if (other === 0) {
      throw new Error("Division by zero");
    }
    return thisVal / other;
  }

  /**
   * Real number exponentiation.
   * @param other - Parameter
   * @returns Result value
   */
  exponent(other: number): number {
    const thisVal = this.value || 0;
    return Math.pow(thisVal, other);
  }

  /**
   * Returns True if current Real is less than \`_other_\`.
   * @param other - Parameter
   * @returns Result value
   */
  override less_than(other: Ordered): Boolean {
    if (other instanceof Real) {
      const thisVal = this.value || 0;
      const otherVal = other.value || 0;
      return new Boolean(thisVal < otherVal);
    }
    return new Boolean(false);
  }

  /**
   * Generate negative of current Real value.
   * @returns Result value
   */
  negative(): number {
    const thisVal = this.value || 0;
    return -thisVal;
  }

  /**
   * Value equality: return True if \`this\` and \`_other_\` are attached to objects considered to be equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (other instanceof Real) {
      const thisVal = this.value || 0;
      const otherVal = other.value || 0;
      return new Boolean(thisVal === otherVal);
    }
    return new Boolean(false);
  }
}

/**
 * Type representing minimal interface of built-in Integer64 type.
 */
export class Integer64 extends Ordered_Numeric {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("INTEGER64", Integer64);
  }

  /**
   * The underlying primitive value.
   */
  value?: number;

  /**
   * Creates a new Integer64 instance.
   * @param val - The primitive value to wrap
   */
  constructor(val?: number) {
    super();
    if (val !== undefined && val !== null && !Number.isInteger(val)) {
      throw new Error(`Integer64 value must be an integer, got: ${val}`);
    }
    this.value = val;
  }

  /**
   * Creates a Integer64 instance from a primitive value.
   * @param val - The primitive value to wrap
   * @returns A new Integer64 instance
   */
  static from(val?: number): Integer64 {
    return new Integer64(val);
  }

  /**
   * Compares this Integer64 with another for value equality.
   * @param other - The object to compare with
   * @returns true if the values are equal
   */
  is_equal(other: any): Boolean {
    if (other instanceof Integer64) {
      return new Boolean(this.value === other.value);
    }
    return new Boolean(false);
  }

  /**
   * Lexical comparison for large integers.
   * @param other - Parameter
   * @returns Result value
   */
  less_than(other: Ordered): Boolean {
    if (!(other instanceof Integer64)) {
      throw new Error("Cannot compare Integer64 with non-Integer64");
    }
    const thisVal = this.value || 0;
    const otherVal = other.value || 0;
    return new Boolean(thisVal < otherVal);
  }

  /**
   * Large integer addition.
   * @param other - Parameter
   * @returns Result value
   */
  add(other: Integer): Integer64 {
    const thisVal = this.value || 0;
    const otherVal = other.value || 0;
    return Integer64.from(thisVal + otherVal);
  }

  /**
   * Large integer subtraction.
   * @param other - Parameter
   * @returns Result value
   */
  subtract(other: Integer): Integer64 {
    const thisVal = this.value || 0;
    const otherVal = other.value || 0;
    return Integer64.from(thisVal - otherVal);
  }

  /**
   * Large integer multiplication.
   * @param other - Parameter
   * @returns Result value
   */
  multiply(other: Integer): Integer64 {
    const thisVal = this.value || 0;
    const otherVal = other.value || 0;
    return Integer64.from(thisVal * otherVal);
  }

  /**
   * Large integer division.
   * @param other - Parameter
   * @returns Result value
   */
  divide(other: Integer): number {
    const thisVal = this.value || 0;
    const otherVal = other.value || 1;
    if (otherVal === 0) {
      throw new Error("Division by zero");
    }
    return thisVal / otherVal;
  }

  /**
   * Large integer exponentiation.
   * @param other - Parameter
   * @returns Result value
   */
  exponent(other: number): number {
    const thisVal = this.value || 0;
    return Math.pow(thisVal, other);
  }

  /**
   * Large integer modulus.
   * @param mod - Parameter
   * @returns Result value
   */
  modulo(mod: Integer): Integer64 {
    const thisVal = this.value || 0;
    const modVal = mod.value || 1;
    if (modVal === 0) {
      throw new Error("Modulo by zero");
    }
    return Integer64.from(thisVal % modVal);
  }

  /**
   * Generate negative of current Integer value.
   * @returns Result value
   */
  negative(): Integer64 {
    return Integer64.from(-(this.value || 0));
  }

  /**
   * Reference equality for reference types, value equality for value types.
   * @param other - Parameter
   * @returns Result value
   */
  override equal(other: Any): Boolean {
    // Reference equality - same object
    return new Boolean(this === other);
  }
}

export abstract class Comparable {
}
